import { createElement } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Crown } from 'lucide-react';
import type { CreateTaskInput, UpdateTaskInput, TaskStatus, TaskPriority, TaskCategory, TaskAssignee } from '@frappi/shared';
import { api, ApiRequestError } from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

export interface TaskFilters {
  status?: TaskStatus;
  priority?: TaskPriority;
  studentId?: string;
  category?: TaskCategory;
  assignedTo?: TaskAssignee;
  dueBefore?: string;
  dueAfter?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (filters?: TaskFilters) => [...taskKeys.lists(), filters] as const,
  details: () => [...taskKeys.all, 'detail'] as const,
  detail: (id: string) => [...taskKeys.details(), id] as const,
  overdue: () => [...taskKeys.all, 'overdue'] as const,
  upcoming: () => [...taskKeys.all, 'upcoming'] as const,
};

export function useTasks(filters?: TaskFilters) {
  return useQuery({
    queryKey: taskKeys.list(filters),
    queryFn: () => api.tasks.list(filters),
  });
}

export function useTask(id: string) {
  return useQuery({
    queryKey: taskKeys.detail(id),
    queryFn: () => api.tasks.get(id),
    enabled: !!id,
  });
}

export function useOverdueTasks() {
  return useQuery({
    queryKey: taskKeys.overdue(),
    queryFn: () => api.tasks.getOverdue(),
  });
}

export function useUpcomingTasks() {
  return useQuery({
    queryKey: taskKeys.upcoming(),
    queryFn: () => api.tasks.getUpcoming(),
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('tasks');

  return useMutation({
    mutationFn: (data: CreateTaskInput) => api.tasks.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      toast.success(t('messages.created'));
    },
    onError: (error: Error) => {
      if (error instanceof ApiRequestError && error.isQuotaExceeded()) {
        toast(t('quotaExceeded.title'), {
          description: t('quotaExceeded.description'),
          icon: createElement(Crown, { className: 'h-5 w-5 text-amber-500' }),
          action: {
            label: t('quotaExceeded.upgrade'),
            onClick: () => { useUIStore.getState().setUpgradeDialogOpen(true); },
          },
          duration: 8000,
        });
        return;
      }
      toast.error(error.message);
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('tasks');

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaskInput }) =>
      api.tasks.update(id, data),
    onSuccess: (task) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.setQueryData(taskKeys.detail(task.id), task);
      toast.success(t('messages.updated'));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('tasks');

  return useMutation({
    mutationFn: (id: string) => api.tasks.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      toast.success(t('messages.deleted'));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useCompleteTask() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('tasks');

  return useMutation({
    mutationFn: (id: string) => api.tasks.complete(id),
    onSuccess: (task) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.setQueryData(taskKeys.detail(task.id), task);
      toast.success(t('messages.completed'));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useBulkDeleteTasks() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('tasks');

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map((id) => api.tasks.delete(id)));
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected').length;
      return { succeeded, failed };
    },
    onSuccess: ({ succeeded, failed }) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      if (failed > 0) {
        toast.info(`${succeeded} tareas eliminadas, ${failed} fallaron`);
      } else {
        toast.success(t('messages.bulkDeleted', { count: succeeded }));
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateTaskStatus() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('tasks');

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: TaskStatus }) =>
      api.tasks.updateStatus(id, status),
    onSuccess: (task) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.setQueryData(taskKeys.detail(task.id), task);
      toast.success(t('messages.updated'));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
